/**
 * PG사 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { pgService } from '../../services/pgService';
import { PgProvider, PgProviderFilter } from '../../types/pg';

const PgProvidersList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PgProviderFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pgProviders', filter],
    queryFn: () => pgService.getProviders(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<PgProvider>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'provider_name',
      label: 'PG사명',
      render: (provider) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/pg/providers/${provider.id}`)}
        >
          {provider.provider_name}
        </span>
      ),
    },
    {
      key: 'provider_code',
      label: 'PG사 코드',
      render: (provider) => (
        <span style={{ color: '#ccc' }}>{provider.provider_code}</span>
      ),
    },
    {
      key: 'commission_rate',
      label: '수수료율',
      render: (provider) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {provider.commission_rate}%
        </span>
      ),
    },
    {
      key: 'api_key',
      label: 'API 설정',
      render: (provider) => (
        <span style={{ color: '#ccc' }}>
          {provider.api_key ? '설정됨' : '미설정'}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (provider) => getStatusBadge(provider.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: 'PG사명, 코드로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (provider: PgProvider) => {
    navigate(`/admin/pg/providers/${provider.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>PG사 관리</h1>
          <div>
            <Button variant="info" onClick={() => navigate('/admin/pg/settlements')} className="me-2">
              정산 관리
            </Button>
            <Button variant="primary" onClick={() => navigate('/admin/pg/providers/new')}>
              PG사 등록
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.providers || []}
              loading={isLoading}
              emptyMessage="PG사가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PgProvidersList;

